// Application certification review (Agent Platform Phase 9B). Developers run
// the mechanical checks and request review from their Developer Portal; the
// queue lands here. Certifying stamps the badge their portal shows.
import { fmtDate, fmtDateTime } from '../../lib/format';
import React, { useEffect, useState } from 'react';
import {
  Button, Descriptions, Modal, Segmented, Space, Table, Tag, Typography, Input, message,
} from 'antd';
import { ReloadOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import {
  decideCertification, listCertifications, type CertificationApp,
} from '../../services/sandboxService';

const STATUS_TAG: Record<string, React.ReactNode> = {
  in_review: <Tag color="processing">in review</Tag>,
  certified: <Tag color="green">certified</Tag>,
  rejected: <Tag color="red">rejected</Tag>,
  none: <Tag>none</Tag>,
};

const CertificationsPage: React.FC = () => {
  const [status, setStatus] = useState('in_review');
  const [rows, setRows] = useState<CertificationApp[]>([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<CertificationApp | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async (s = status) => {
    setLoading(true);
    try {
      setRows(await listCertifications(s));
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not load certifications.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const decide = async (decision: 'certified' | 'rejected') => {
    if (!detail) return;
    setBusy(true);
    try {
      await decideCertification(detail.id, decision, note || undefined);
      message.success(decision === 'certified' ? 'Application certified.' : 'Application rejected.');
      setDetail(null);
      setNote('');
      load();
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not record the decision.');
    } finally {
      setBusy(false);
    }
  };

  const checks = detail?.certificationEvidence?.checks || {};

  return (
    <div>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          <SafetyCertificateOutlined /> Application certifications
        </Typography.Title>
        <Space>
          <Segmented
            value={status}
            onChange={(v) => setStatus(String(v))}
            options={[
              { label: 'In review', value: 'in_review' },
              { label: 'Certified', value: 'certified' },
              { label: 'Rejected', value: 'rejected' },
            ]}
          />
          <Button icon={<ReloadOutlined />} onClick={() => load()} />
        </Space>
      </Space>
      <Table<CertificationApp>
        rowKey="id"
        loading={loading}
        dataSource={rows}
        pagination={false}
        onRow={(r) => ({ onClick: () => setDetail(r), style: { cursor: 'pointer' } })}
        columns={[
          { title: 'Application', dataIndex: 'name' },
          { title: 'Client', dataIndex: 'clientId', render: (v) => <Typography.Text code>{v}</Typography.Text> },
          { title: 'Kind', dataIndex: 'kind', width: 90, render: (k) => <Tag>{k}</Tag> },
          { title: 'Env', dataIndex: 'environment', width: 100, render: (e) => <Tag color={e === 'sandbox' ? 'orange' : 'blue'}>{e}</Tag> },
          {
            title: 'Checks', width: 110,
            render: (_, r) => (r.certificationEvidence?.passed
              ? <Tag color="green">passing</Tag>
              : <Tag color="red">failing</Tag>),
          },
          { title: 'Status', dataIndex: 'certificationStatus', width: 120, render: (s) => STATUS_TAG[s] || s },
          {
            title: 'Requested', dataIndex: 'certificationRequestedAt', width: 180,
            render: (v?: string) => (v ? fmtDateTime(v) : '—'),
          },
        ]}
      />
      <Modal
        title={detail ? `Review: ${detail.name}` : ''}
        open={!!detail}
        onCancel={() => setDetail(null)}
        footer={detail?.certificationStatus === 'in_review' ? [
          <Button key="reject" danger loading={busy} onClick={() => decide('rejected')}>Reject</Button>,
          <Button key="ok" type="primary" loading={busy} onClick={() => decide('certified')}>Certify</Button>,
        ] : null}
      >
        {detail && (
          <>
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="Client id">{detail.clientId}</Descriptions.Item>
              <Descriptions.Item label="Description">{detail.description || '—'}</Descriptions.Item>
              <Descriptions.Item label="Checks ran">
                {detail.certificationEvidence?.ranAt
                  ? fmtDateTime(detail.certificationEvidence.ranAt)
                  : 'never'}
              </Descriptions.Item>
              {Object.entries(checks).map(([k, v]) => (
                <Descriptions.Item key={k} label={k.replace(/_/g, ' ')}>
                  {v.passed ? <Tag color="green">pass</Tag> : <Tag color="red">fail</Tag>}
                  {typeof v.statusCode === 'number' ? ` HTTP ${v.statusCode}` : ''}
                  {v.error ? ` — ${v.error}` : ''}
                </Descriptions.Item>
              ))}
              {detail.certificationNote && (
                <Descriptions.Item label="Decision note">{detail.certificationNote}</Descriptions.Item>
              )}
            </Descriptions>
            {detail.certificationStatus === 'in_review' && (
              <Input.TextArea
                style={{ marginTop: 12 }}
                rows={2}
                placeholder="Decision note (shown to the developer)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            )}
          </>
        )}
      </Modal>
    </div>
  );
};

export default CertificationsPage;
export { CertificationsPage };
